/**
 * Learning / University API — typed wrappers + TanStack Query cache keys.
 *
 * Mirrors apps/chaos-gateway/src/routes/learning.ts. The gateway already
 * translates snake_case → camelCase for the endpoints below, so callers
 * see idiomatic TS shapes from @chaos/types/learning.
 *
 * Pass-through endpoints (skill-effectiveness, skill-export, marketplace)
 * still arrive in snake_case from the upstream Python service — those
 * wrappers preserve the original shape and surface it to the caller.
 */
import type {
  AbTest,
  AbTestDetail,
  AbTestEvaluation,
  AbTestsList,
  AppendIterationRequest,
  ApproveExampleRequest,
  ApproveExampleResponse,
  ConcludeAbTestRequest,
  CreateAbTestRequest,
  CreateTrainingSessionRequest,
  ExportSkillRequest,
  ExportSkillResponse,
  InstallMarketplaceSkillResponse,
  MarketplaceListing,
  MarketplaceSkillsList,
  PatternClustersResponse,
  PublishToMarketplaceRequest,
  PublishToMarketplaceResponse,
  RedactionPreview,
  SkillCatalogList,
  SkillDetail,
  SkillEffectivenessDetailed,
  SkillVersion,
  TrainingSessionDetail,
  TrainingSessionSummary,
  TrainingSessionsList,
  TunerEdit,
  TunerSummary,
  TunersList,
  UniversitySummary,
} from '@chaos/types/learning';

import { apiJson } from './client';

export interface ListSkillsParams {
  q?: string;
  department?: string;
  status?: string;
  sort?: 'updated' | 'effectiveness' | 'attempts' | 'name';
  limit?: number;
}

export interface ListTrainingSessionsParams {
  tunerId?: string;
  skillId?: string;
  status?: 'planned' | 'active' | 'awaiting_tuner' | 'completed' | 'abandoned';
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const learningApi = {
  // ── Skills ──────────────────────────────────────────────────────────
  listSkills(params: ListSkillsParams = {}): Promise<SkillCatalogList> {
    return apiJson<SkillCatalogList>(
      `/learning/skills${qs(params as Record<string, string | number | undefined>)}`,
    );
  },
  getSkill(keyOrId: string): Promise<SkillDetail> {
    return apiJson<SkillDetail>(`/learning/skills/${encodeURIComponent(keyOrId)}`);
  },
  listSkillVersions(skillId: string): Promise<SkillVersion[]> {
    return apiJson<SkillVersion[]>(`/learning/skills/${encodeURIComponent(skillId)}/versions`);
  },

  // ── Tuners ──────────────────────────────────────────────────────────
  listTuners(): Promise<TunersList> {
    return apiJson<TunersList>('/learning/tuners');
  },
  getTuner(tunerId: string): Promise<TunerSummary> {
    return apiJson<TunerSummary>(`/learning/tuners/${encodeURIComponent(tunerId)}`);
  },
  listTunerEdits(tunerId: string, limit: number = 50): Promise<TunerEdit[]> {
    return apiJson<TunerEdit[]>(
      `/learning/tuners/${encodeURIComponent(tunerId)}/edits${qs({ limit })}`,
    );
  },

  // ── Training sessions ───────────────────────────────────────────────
  listTrainingSessions(params: ListTrainingSessionsParams = {}): Promise<TrainingSessionsList> {
    return apiJson<TrainingSessionsList>(
      `/learning/training-sessions${qs(params as Record<string, string | number | undefined>)}`,
    );
  },
  getTrainingSession(sessionId: string): Promise<TrainingSessionDetail> {
    return apiJson<TrainingSessionDetail>(
      `/learning/training-sessions/${encodeURIComponent(sessionId)}`,
    );
  },
  createTrainingSession(input: CreateTrainingSessionRequest): Promise<TrainingSessionSummary> {
    return apiJson<TrainingSessionSummary>('/learning/training-sessions', {
      method: 'POST',
      json: input,
    });
  },
  appendIteration(
    sessionId: string,
    input: AppendIterationRequest,
  ): Promise<TrainingSessionDetail> {
    return apiJson<TrainingSessionDetail>(
      `/learning/training-sessions/${encodeURIComponent(sessionId)}/iterations`,
      { method: 'POST', json: input },
    );
  },
  approveExample(sessionId: string, input: ApproveExampleRequest): Promise<ApproveExampleResponse> {
    return apiJson<ApproveExampleResponse>(
      `/learning/training-sessions/${encodeURIComponent(sessionId)}/approve`,
      { method: 'POST', json: input },
    );
  },

  // ── Effectiveness (pass-through, snake_case) ────────────────────────
  getEffectiveness(skillId: string, version: string): Promise<SkillEffectivenessDetailed> {
    return apiJson<SkillEffectivenessDetailed>(
      `/learning/skill-effectiveness/${encodeURIComponent(skillId)}/${encodeURIComponent(version)}`,
    );
  },

  // ── Export (pass-through) ───────────────────────────────────────────
  exportSkill(req: ExportSkillRequest): Promise<ExportSkillResponse> {
    return apiJson<ExportSkillResponse>('/learning/skill-export', {
      method: 'POST',
      json: req,
    });
  },

  // ── Marketplace (pass-through) ──────────────────────────────────────
  listMarketplace(statusFilter?: string): Promise<MarketplaceListing[]> {
    return apiJson<MarketplaceListing[]>(
      `/learning/marketplace${qs({ status_filter: statusFilter })}`,
    );
  },

  // ── Summary (gateway-aggregated KPI) ────────────────────────────────
  getSummary(): Promise<UniversitySummary> {
    return apiJson<UniversitySummary>('/learning/summary');
  },

  // ── Phase 9 — patterns ──────────────────────────────────────────────
  getPatterns(
    params: {
      skillId?: string;
      days?: number;
      limit?: number;
      distanceThreshold?: number;
      minClusterSize?: number;
    } = {},
  ): Promise<PatternClustersResponse> {
    return apiJson<PatternClustersResponse>(
      `/learning/patterns${qs(params as Record<string, string | number | undefined>)}`,
    );
  },

  // ── Phase 9 — A/B tests ─────────────────────────────────────────────
  listAbTests(): Promise<AbTestsList> {
    return apiJson<AbTestsList>('/learning/ab-tests');
  },
  getAbTest(testId: string): Promise<AbTestDetail> {
    return apiJson<AbTestDetail>(`/learning/ab-tests/${encodeURIComponent(testId)}`);
  },
  createAbTest(input: CreateAbTestRequest): Promise<AbTest> {
    return apiJson<AbTest>('/learning/ab-tests', { method: 'POST', json: input });
  },
  concludeAbTest(
    testId: string,
    input: ConcludeAbTestRequest,
  ): Promise<{ ok: boolean; evaluation: AbTestEvaluation | null }> {
    return apiJson<{ ok: boolean; evaluation: AbTestEvaluation | null }>(
      `/learning/ab-tests/${encodeURIComponent(testId)}/conclude`,
      { method: 'POST', json: input },
    );
  },

  // ── Phase 9 — Marketplace browseable surface ────────────────────────
  listMarketplaceSkills(
    params: {
      q?: string;
      category?: string;
      sort?: 'updated' | 'installs' | 'effectiveness' | 'name';
      limit?: number;
      includeAllStatuses?: boolean;
    } = {},
  ): Promise<MarketplaceSkillsList> {
    const raw: Record<string, string | number | undefined> = {};
    if (params.q) raw.q = params.q;
    if (params.category) raw.category = params.category;
    if (params.sort) raw.sort = params.sort;
    if (params.limit) raw.limit = params.limit;
    if (params.includeAllStatuses) raw.includeAllStatuses = 'true';
    return apiJson<MarketplaceSkillsList>(`/learning/marketplace/skills${qs(raw)}`);
  },
  getRedactionPreview(
    skillId: string,
    params: { format?: string; version?: string } = {},
  ): Promise<RedactionPreview> {
    const raw: Record<string, string | number | undefined> = {};
    if (params.format) raw.format = params.format;
    if (params.version) raw.version = params.version;
    return apiJson<RedactionPreview>(
      `/learning/marketplace/skills/${encodeURIComponent(skillId)}/redaction-preview${qs(raw)}`,
    );
  },
  publishToMarketplace(input: PublishToMarketplaceRequest): Promise<PublishToMarketplaceResponse> {
    return apiJson<PublishToMarketplaceResponse>('/learning/marketplace/publish', {
      method: 'POST',
      json: input,
    });
  },
  installMarketplaceSkill(skillId: string): Promise<InstallMarketplaceSkillResponse> {
    return apiJson<InstallMarketplaceSkillResponse>(
      `/learning/marketplace/skills/${encodeURIComponent(skillId)}/install`,
      { method: 'POST', json: { skillId } },
    );
  },
};

export const learningKeys = {
  all: ['learning'] as const,
  skills: ['learning', 'skills'] as const,
  skillsList: (params: ListSkillsParams) => [...learningKeys.skills, 'list', params] as const,
  skill: (keyOrId: string) => [...learningKeys.skills, 'detail', keyOrId] as const,
  skillVersions: (skillId: string) => [...learningKeys.skills, 'versions', skillId] as const,
  effectiveness: (skillId: string, version: string) =>
    [...learningKeys.skills, 'effectiveness', skillId, version] as const,

  tuners: ['learning', 'tuners'] as const,
  tunersList: () => [...learningKeys.tuners, 'list'] as const,
  tuner: (tunerId: string) => [...learningKeys.tuners, 'detail', tunerId] as const,
  tunerEdits: (tunerId: string, limit?: number) =>
    [...learningKeys.tuners, 'edits', tunerId, limit ?? null] as const,

  trainingSessions: ['learning', 'training-sessions'] as const,
  trainingSessionsList: (params: ListTrainingSessionsParams) =>
    [...learningKeys.trainingSessions, 'list', params] as const,
  trainingSession: (sessionId: string) =>
    [...learningKeys.trainingSessions, 'detail', sessionId] as const,

  marketplace: ['learning', 'marketplace'] as const,
  marketplaceList: (statusFilter?: string) =>
    [...learningKeys.marketplace, 'list', statusFilter ?? null] as const,

  // Phase 9 — Marketplace browseable list (different endpoint).
  marketplaceSkills: (params: Record<string, string | number | boolean | undefined> = {}) =>
    [...learningKeys.marketplace, 'skills', params] as const,
  redactionPreview: (skillId: string, format: string) =>
    [...learningKeys.marketplace, 'redaction-preview', skillId, format] as const,

  // Phase 9 — Patterns + A/B tests.
  patterns: (params: Record<string, string | number | undefined> = {}) =>
    ['learning', 'patterns', params] as const,
  abTests: ['learning', 'ab-tests'] as const,
  abTestsList: () => [...learningKeys.abTests, 'list'] as const,
  abTest: (testId: string) => [...learningKeys.abTests, 'detail', testId] as const,

  summary: () => [...learningKeys.all, 'summary'] as const,
};
